import { 
  UserIcon,
  ShoppingBagIcon,
  TrendingUpIcon,
  StarIcon
} from '../Icons'
import { Product } from '../../data/products'

interface ProfileScreenProps {
  orders: Product[]
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  blue: '#3b82f6',
  red: '#ef4444',
}

export default function ProfileScreen({ orders }: ProfileScreenProps) {
  const totalSpent = orders.reduce((sum, product) => {
    const priceStr = (product.price || '0').replace(/[^\d]/g, '')
    return sum + (parseInt(priceStr) || 0)
  }, 0)

  const stats = [
    {
      icon: <ShoppingBagIcon size={20} color={colors.accentLight} />,
      label: 'Заказов',
      value: orders.length,
      color: colors.accentLight,
    },
    {
      icon: <TrendingUpIcon size={20} color={colors.blue} />,
      label: 'Потрачено',
      value: `${(totalSpent / 1000).toFixed(1)}k ₽`,
      color: colors.blue,
    },
    {
      icon: <StarIcon size={20} color={colors.yellow} />,
      label: 'Рейтинг',
      value: '4.9',
      color: colors.yellow,
    },
  ]

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      paddingBottom: '100px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Заголовок */}
      <div style={{
        marginBottom: '24px',
      }}>
        <div style={{
          color: colors.text,
          fontSize: '28px',
          fontWeight: 700,
          marginBottom: '8px',
        }}>
          Профиль
        </div>
        <div style={{
          color: colors.textMuted,
          fontSize: '14px',
        }}>
          Ваша статистика и данные
        </div>
      </div>

      {/* Карточка пользователя */}
      <div style={{
        background: `linear-gradient(135deg, ${colors.accent}20, transparent)`,
        border: `1px solid ${colors.accentBorder}`,
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        textAlign: 'center',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: colors.nestedBg,
          border: `2px solid ${colors.accentBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <UserIcon size={40} color={colors.accentLight} />
        </div>

        <div style={{
          color: colors.text,
          fontSize: '22px',
          fontWeight: 600,
          marginBottom: '6px',
        }}>
          Пользователь AiVinity
        </div>

        <div style={{
          color: colors.textMuted,
          fontSize: '13px',
        }}>
          ID: #12345678
        </div>
      </div>

      {/* Статистика */}
      <div style={{
        marginBottom: '24px',
      }}>
        <div style={{
          color: colors.text,
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '12px',
        }}>
          Статистика
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
        }}>
          {stats.map((stat, index) => (
            <div
              key={index}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: '12px',
                padding: '16px 12px',
                textAlign: 'center',
              }}
            >
              <div style={{
                marginBottom: '8px',
                display: 'flex',
                justifyContent: 'center',
              }}>
                {stat.icon}
              </div>
              <div style={{
                color: stat.color,
                fontSize: '20px',
                fontWeight: 700,
                marginBottom: '4px',
              }}>
                {stat.value}
              </div>
              <div style={{
                color: colors.textMuted,
                fontSize: '11px',
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Последние заказы */}
      <div>
        <div style={{
          color: colors.text,
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '12px',
        }}>
          Последние заказы
        </div>

        {orders.length === 0 ? (
          <div style={{
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
          }}>
            <div style={{
              color: colors.textMuted,
              fontSize: '13px',
            }}>
              Пока нет заказов
            </div>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
            {orders.slice(0, 3).map((product, index) => (
              <div
                key={`${product.id}-${index}`}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.cardBorder}`,
                  borderRadius: '10px',
                  padding: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: `${colors.accent}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ShoppingBagIcon size={18} color={colors.accentLight} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{
                    color: colors.text,
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '2px',
                  }}>
                    {product.title}
                  </div>
                  <div style={{
                    color: colors.textMuted,
                    fontSize: '11px',
                  }}>
                    {product.price}
                  </div>
                </div>

                <div style={{
                  background: `${colors.green}20`,
                  borderRadius: '6px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: colors.green,
                }}>
                  Оплачено
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Достижения */}
      <div style={{
        marginTop: '24px',
      }}>
        <div style={{
          color: colors.text,
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '12px',
        }}>
          Достижения
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '10px',
        }}>
          {[
            { emoji: '🎓', active: orders.length > 0 },
            { emoji: '💻', active: orders.length > 2 },
            { emoji: '🤖', active: orders.length > 5 },
            { emoji: '⭐', active: orders.length > 10 },
          ].map((achievement, index) => (
            <div
              key={index}
              style={{
                background: achievement.active ? colors.cardBg : colors.nestedBg,
                border: `1px solid ${achievement.active ? colors.accentBorder : colors.cardBorder}`,
                borderRadius: '10px',
                padding: '12px',
                textAlign: 'center',
                fontSize: '24px',
                opacity: achievement.active ? 1 : 0.3,
              }}
            >
              {achievement.emoji}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
